import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FinancialOverview({
  revenue = "₹0",
  avgInvoiceValue = "₹0",
  highestInvoice = "₹0",
  monthlyData = []
}) {
  const rawData = Array.isArray(monthlyData) && monthlyData.length > 0
    ? monthlyData
    : [{ month: 'Aug 1', value: 0 }];

  // Smart Aggregation for Wide Ranges (e.g. multi-year/multi-month)
  const chartData = useMemo(() => {
    if (rawData.length <= 35) {
      return rawData.map(d => ({
        ...d,
        displayLabel: d.month || d.fullDate || 'N/A',
        tooltipLabel: d.fullDate || d.month || 'Day',
        value: Number(d.value) || 0
      }));
    }

    // Group by month when > 35 points
    const monthMap = new Map();
    rawData.forEach(item => {
      let key;
      let label;
      let fullLabel;

      if (item.fullDate) {
        const d = new Date(item.fullDate);
        if (!isNaN(d.getTime())) {
          key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          label = `${MONTH_NAMES[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`;
          fullLabel = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
        }
      }

      if (!key) {
        key = item.month || 'Unknown';
        label = item.month || 'Unknown';
        fullLabel = item.month || 'Unknown';
      }

      if (!monthMap.has(key)) {
        monthMap.set(key, {
          displayLabel: label,
          tooltipLabel: fullLabel,
          value: 0
        });
      }

      monthMap.get(key).value += (Number(item.value) || 0);
    });

    return Array.from(monthMap.values());
  }, [rawData]);

  // Compute dynamic max value for Y-axis so curve rises up
  const maxVal = Math.max(...chartData.map((d) => Number(d.value) || 0), 1000);
  const yDomainMax = Math.ceil(maxVal * 1.2);
  const showDots = chartData.length <= 35;

  return (
    <div className="dash-card p-2 d-flex flex-column" style={{ height: '100%' }}>
      {/* Header */}
      <div className="px-1 mb-1">
        <h6 className="m-0 fw-bold" style={{ fontSize: '12.5px', color: '#0f172a' }}>
          Financial Overview
        </h6>
      </div>

      {/* Body: Chart Left + Stats Right */}
      <div className="d-flex align-items-center" style={{ flex: 1 }}>
        {/* Area Chart */}
        <div style={{ width: '56%', height: 155 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 5, left: -25, bottom: 0 }}>
              <defs>
                <linearGradient id="finAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="5%" stopColor="#0d7a71" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#0d7a71" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="displayLabel"
                tick={{ fontSize: 8, fill: '#94a3b8', fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                minTickGap={14}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, yDomainMax]}
                tickCount={5}
                tick={{ fontSize: 7.5, fill: '#94a3b8', fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v)}
              />
              <Tooltip
                contentStyle={{
                  fontSize: '10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  padding: '4px 8px'
                }}
                formatter={(value) => ['₹' + Number(value).toLocaleString('en-IN'), 'Revenue']}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.tooltipLabel || label}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#064e48"
                strokeWidth={2.2}
                fill="url(#finAreaGrad)"
                dot={showDots ? { r: 2.5, fill: '#064e48', strokeWidth: 0 } : false}
                activeDot={{ r: 4, fill: '#064e48', strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Right Stats */}
        <div className="d-flex flex-column justify-content-center ps-3" style={{ width: '44%', gap: '10px' }}>
          {/* Revenue */}
          <div className="d-flex align-items-start gap-2">
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%',
              backgroundColor: '#064e48', marginTop: '5px', flexShrink: 0
            }}></div>
            <div>
              <div style={{ fontSize: '9.5px', color: '#64748b', fontWeight: '500', lineHeight: '1.2' }}>Revenue</div>
              <div style={{ fontSize: '15px', color: '#0f172a', fontWeight: '800', lineHeight: '1.3' }}>{revenue}</div>
            </div>
          </div>

          {/* Avg Invoice Value */}
          <div className="d-flex align-items-start gap-2">
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%',
              backgroundColor: '#0d7a71', marginTop: '5px', flexShrink: 0
            }}></div>
            <div>
              <div style={{ fontSize: '9.5px', color: '#64748b', fontWeight: '500', lineHeight: '1.2' }}>Avg. Invoice Value</div>
              <div style={{ fontSize: '12.5px', color: '#1e293b', fontWeight: '700', lineHeight: '1.3' }}>{avgInvoiceValue}</div>
            </div>
          </div>

          {/* Highest Invoice */}
          <div className="d-flex align-items-start gap-2">
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%',
              backgroundColor: '#15803d', marginTop: '5px', flexShrink: 0
            }}></div>
            <div>
              <div style={{ fontSize: '9.5px', color: '#64748b', fontWeight: '500', lineHeight: '1.2' }}>Highest Invoice</div>
              <div style={{ fontSize: '12.5px', color: '#1e293b', fontWeight: '700', lineHeight: '1.3' }}>{highestInvoice}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
